import Decimal from 'decimal.js';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { getCargoOperationForCargo, getLastBerthCallInPortCall, isBeforeOrEqual } from '../stowageRotationUtils';
import { isShipFigureAdjusted, stowedWeightInTanks } from './stowageOperationUtils';
import { getTankContentInBerthCall } from 'src/utils/stowage/manifest/cargoManifestUtils';

/**
 * Set this to true to enable console logging for specific tank operations (for debugging purposes)
 */
const logSpecificTankOperations = false;

/**
 * Check if a loading operation occurred before the transit port call (when there's no berth call)
 * Uses the port call sequence in the rotation to determine order
 */
const isLoadingBeforeTransitPortCall = (
    stowagePlan: StowagePlan,
    loadingOp: RotationCargoOperation,
    transitOp: RotationCargoOperation
): boolean => {
    // Find the index of the loading operation's port call in the rotation
    const loadingPortCallIndex = stowagePlan.rotation.findIndex((p) => p.portCallGuid === loadingOp.portCallGuid);
    // Find the index of the transit operation's port call in the rotation
    const transitPortCallIndex = stowagePlan.rotation.findIndex((p) => p.portCallGuid === transitOp.portCallGuid);

    // Loading should be included if it's before or at the same port call as the transit
    // (Loading at the same port call would have happened before transit)
    return loadingPortCallIndex !== -1 && transitPortCallIndex !== -1 && loadingPortCallIndex <= transitPortCallIndex;
};

/**
 * Check if an unloading operation occurred before the transit port call (when there's no berth call)
 * Uses the port call sequence in the rotation to determine order
 */
const isUnloadingBeforeTransitPortCall = (
    stowagePlan: StowagePlan,
    unloadingOp: RotationCargoOperation,
    transitOp: RotationCargoOperation
): boolean => {
    // Find the index of the unloading operation's port call in the rotation
    const unloadingPortCallIndex = stowagePlan.rotation.findIndex((p) => p.portCallGuid === unloadingOp.portCallGuid);
    // Find the index of the transit operation's port call in the rotation
    const transitPortCallIndex = stowagePlan.rotation.findIndex((p) => p.portCallGuid === transitOp.portCallGuid);

    // Unloading should be included only if it's strictly before the transit port call
    // (Unloading at the same port call would be after transit)
    return unloadingPortCallIndex !== -1 && transitPortCallIndex !== -1 && unloadingPortCallIndex < transitPortCallIndex;
};

/**
 * Sort cargo operations by their sequence number in ascending order
 * @param operations - Array of cargo operations to sort
 * @returns Sorted array of cargo operations
 */
const sortCargoOperationsBySequence = (operations: RotationCargoOperation[]): RotationCargoOperation[] => {
    return operations.sort((a, b) => (a.cargoOperationSequenceNumber || 0) - (b.cargoOperationSequenceNumber || 0));
};

export const getShipFigureCssClass = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): string => {
    if (isShipFigureAdjusted(stowagePlan, cargoOperation)) {
        return 'adjusted-ship-figure';
    } else {
        return '';
    }
};

/**
 * Calculate ship figure for a cargo operation based on operation type
 * @param stowagePlan - The stowage plan containing stowage items
 * @param cargoOperation - The cargo operation to calculate ship figure for
 * @returns Decimal - The aggregated ship figure for the operation
 */
export const getShipFigureForOperationType = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): Decimal => {
    // Collect all related stowage items for the cargo operation
    const relatedStowageItems = stowagePlan.stowageItems.filter((item) => item.cargoOperationGuid === cargoOperation.cargoOperationGuid);

    // For transit operations we may not have direct stowage items, so don't early-return.
    if (relatedStowageItems.length === 0 && cargoOperation.operationType !== 'Transit') {
        return new Decimal(0);
    }

    if (cargoOperation.operationType === 'Loading') {
        return calculateLoadingOperationShipFigure(relatedStowageItems);
    } else if (cargoOperation.operationType === 'Unloading') {
        return calculateUnloadingOperationShipFigure(stowagePlan, cargoOperation, relatedStowageItems);
    } else if (cargoOperation.operationType === 'Transit') {
        // NOTE: Transit operations don't have direct stowage items in stowagePlan.stowageItems.
        // We need to calculate current ship figure by summing up loading operations, then subtracting unloading operations.

        // Get all operations for this cargo
        const allCargoOps = getCargoOperationForCargo(stowagePlan, cargoOperation.cargo);

        const portCall = stowagePlan.rotation.find((p) => p.portCallGuid === cargoOperation.portCallGuid);
        const currentBerthCall = getLastBerthCallInPortCall(portCall);

        // Calculate total loaded weight (ship figure) from loading operations that occurred before this transit
        let totalLoaded = new Decimal(0);
        allCargoOps
            .filter((op) => op.operationType === 'Loading')
            .forEach((op) => {
                // If currentBerthCall is undefined (port call without berth call), include all loading operations
                // that happened before this port call in the rotation
                const shouldInclude = currentBerthCall
                    ? isBeforeOrEqual(stowagePlan.berthCalls, op.berthCall, currentBerthCall)
                    : isLoadingBeforeTransitPortCall(stowagePlan, op, cargoOperation);

                if (shouldInclude) {
                    const items = stowagePlan.stowageItems.filter((i) => i.cargoOperationGuid === op.cargoOperationGuid);
                    totalLoaded = totalLoaded.plus(calculateLoadingOperationShipFigure(items));
                }
            });

        // Calculate total unloaded weight from unloading operations that occurred before this transit
        // For transit calculations, we need the actual weight unloaded, not the remaining ship figure
        let totalUnloaded = new Decimal(0);
        allCargoOps
            .filter((op) => op.operationType === 'Unloading')
            .forEach((op) => {
                // If currentBerthCall is undefined (port call without berth call), include all unloading operations
                // that happened before this port call in the rotation
                const shouldInclude = currentBerthCall
                    ? isBeforeOrEqual(stowagePlan.berthCalls, op.berthCall, currentBerthCall)
                    : isUnloadingBeforeTransitPortCall(stowagePlan, op, cargoOperation);

                if (shouldInclude) {
                    // For transit calculations, sum the actual weight unloaded, not the calculated ship figure
                    const items = stowagePlan.stowageItems.filter((i) => i.cargoOperationGuid === op.cargoOperationGuid);
                    let unloadedWeight = new Decimal(0);
                    items.forEach((item) => {
                        // Use ship figure if it exists, otherwise use weight
                        if (item.shipFigure !== null && item.shipFigure !== undefined) {
                            unloadedWeight = unloadedWeight.plus(new Decimal(item.shipFigure).abs());
                        } else if (item.weight) {
                            unloadedWeight = unloadedWeight.plus(item.weight.abs());
                        }
                    });
                    totalUnloaded = totalUnloaded.plus(unloadedWeight);
                }
            });

        // Transit ship figure = Total loaded - Total unloaded (up to this point)
        const transitFigure = totalLoaded.minus(totalUnloaded);

        // Ensure we don't return negative ship figures
        return transitFigure.gt(0) ? transitFigure : new Decimal(0);
    }

    // TODO: Implement for other operation types (Transfer, etc.)
    return new Decimal(0);
};

const calculateLoadingOperationShipFigure = (stowageItems: Stowage[]): Decimal => {
    let aggregatedShipFigure = new Decimal(0);

    stowageItems.forEach((item) => {
        // Use ship figure if it's not null and isShipFigureAdjusted is true
        if (item.shipFigure !== null && item.shipFigure !== undefined && item.isShipFigureAdjusted === true) {
            aggregatedShipFigure = aggregatedShipFigure.plus(item.shipFigure);

            if (logSpecificTankOperations) {
                console.log(`Using ship figure for tank ${item.tank?.tankName}: ${item.shipFigure}`);
            }
        } else {
            // Otherwise use the weight property
            if (item.weight) {
                aggregatedShipFigure = aggregatedShipFigure.plus(item.weight);

                if (logSpecificTankOperations) {
                    console.log(`Using weight for tank ${item.tank?.tankName}: ${item.weight.toNumber()}`);
                }
            }
        }
    });

    if (logSpecificTankOperations) {
        console.log(`Total aggregated ship figure for loading operation: ${aggregatedShipFigure.toNumber()}`);
    }

    return aggregatedShipFigure;
};

const calculateUnloadingOperationShipFigure = (
    stowagePlan: StowagePlan,
    cargoOperation: RotationCargoOperation,
    stowageItems: Stowage[]
): Decimal => {
    // Check if this is the last unloading operation for the cargo
    const isLastUnloadingOperation = checkIfLastUnloadingOperation(stowagePlan, cargoOperation);

    if (isLastUnloadingOperation) {
        return calculateLastUnloadingOperationShipFigure(stowagePlan, cargoOperation, stowageItems);
    } else {
        return calculateRegularUnloadingOperationShipFigure(stowagePlan, cargoOperation, stowageItems);
    }
};

const calculateLastUnloadingOperationShipFigure = (
    stowagePlan: StowagePlan,
    cargoOperation: RotationCargoOperation,
    stowageItems: Stowage[]
): Decimal => {
    let aggregatedShipFigure = new Decimal(0);

    if (logSpecificTankOperations) {
        console.log(`Calculating ship figure for LAST unloading operation: ${cargoOperation.cargoOperationGuid}`);
    }

    const tanksInOperation = stowageItems.filter((item) => item.tank?.tankId).map((item) => item.tank!);

    tanksInOperation.forEach((tank) => {
        const tankShipFigure = calculateLastUnloadingShipFigureForTank(stowagePlan, cargoOperation, tank);
        aggregatedShipFigure = aggregatedShipFigure.plus(tankShipFigure);

        if (logSpecificTankOperations) {
            console.log(`Tank ${tank.tankName}: Ship figure contribution: ${tankShipFigure.toNumber()}`);
        }
    });

    if (logSpecificTankOperations) {
        console.log(`Total aggregated ship figure for LAST unloading operation: ${aggregatedShipFigure.toNumber()}`);
    }

    return aggregatedShipFigure;
};

const calculateLastUnloadingShipFigureForTank = (
    stowagePlan: StowagePlan,
    currentUnloadingOperation: RotationCargoOperation,
    tank: Tank
): Decimal => {
    // Get all operations for the same cargo, sorted by sequence/order
    const cargoOperations = sortCargoOperationsBySequence(getCargoOperationForCargo(stowagePlan, currentUnloadingOperation.cargo));

    // Calculate total loaded into this tank
    let totalLoaded = new Decimal(0);

    // Calculate total unloaded from this tank (excluding current operation)
    let totalPreviouslyUnloaded = new Decimal(0);

    cargoOperations.forEach((operation) => {
        const stowageItemsForTank = stowagePlan.stowageItems.filter(
            (item) => item.cargoOperationGuid === operation.cargoOperationGuid && item.tank?.tankId === tank.tankId
        );

        stowageItemsForTank.forEach((item) => {
            if (operation.operationType === 'Loading') {
                // Aggregate loading operations
                const loadingShipFigure =
                    item.shipFigure !== null && item.shipFigure !== undefined
                        ? new Decimal(item.shipFigure)
                        : item.weight
                            ? item.weight.abs()
                            : new Decimal(0);

                totalLoaded = totalLoaded.plus(loadingShipFigure);

                if (logSpecificTankOperations) {
                    console.log(`Tank ${tank.tankName}: Loading operation ${operation.cargoOperationGuid} added: ${loadingShipFigure.toNumber()}`);
                }
            } else if (operation.operationType === 'Unloading' && operation.cargoOperationGuid !== currentUnloadingOperation.cargoOperationGuid) {
                // Aggregate previous unloading operations (exclude current operation)
                const unloadingShipFigure =
                    item.shipFigure !== null && item.shipFigure !== undefined
                        ? new Decimal(item.shipFigure).abs()
                        : item.weight
                            ? item.weight.abs()
                            : new Decimal(0);

                totalPreviouslyUnloaded = totalPreviouslyUnloaded.plus(unloadingShipFigure);

                if (logSpecificTankOperations) {
                    console.log(
                        `Tank ${tank.tankName}: Previous unloading operation ${operation.cargoOperationGuid
                        } subtracted: ${unloadingShipFigure.toNumber()}`
                    );
                }
            }
        });
    });

    // Final calculation: Total loaded - Total previously unloaded
    const remainingShipFigure = totalLoaded.minus(totalPreviouslyUnloaded);

    if (logSpecificTankOperations) {
        console.log(
            `Tank ${tank.tankName}: Total loaded: ${totalLoaded.toNumber()}, ` +
            `Total previously unloaded: ${totalPreviouslyUnloaded.toNumber()}, ` +
            `Remaining for last unloading: ${remainingShipFigure.toNumber()}`
        );
    }

    // Ensure we don't return negative ship figures
    return remainingShipFigure.gt(0) ? remainingShipFigure : new Decimal(0);
};

const calculateRegularUnloadingOperationShipFigure = (
    stowagePlan: StowagePlan,
    cargoOperation: RotationCargoOperation,
    stowageItems: Stowage[]
): Decimal => {
    let aggregatedShipFigure = new Decimal(0);

    stowageItems.forEach((unloadingItem) => {
        if (!unloadingItem.tank) {
            if (logSpecificTankOperations) {
                console.log(`Skipping unloading item without tank: ${unloadingItem.stowageGuid}`);
            }
            return;
        }

        if (unloadingItem.shipFigure !== null && unloadingItem.shipFigure !== undefined) {
            // Ship figure is set - calculate based on previous operation
            const calculatedShipFigure = calculateUnloadingShipFigureForTank(stowagePlan, cargoOperation, unloadingItem);

            aggregatedShipFigure = aggregatedShipFigure.plus(calculatedShipFigure);

            if (logSpecificTankOperations) {
                console.log(`Using calculated ship figure for tank ${unloadingItem.tank.tankName}: ${calculatedShipFigure.toNumber()}`);
            }
        } else {
            // Ship figure not set - use weight property
            if (unloadingItem.weight) {
                const weightValue = unloadingItem.weight.abs();
                aggregatedShipFigure = aggregatedShipFigure.plus(weightValue);

                if (logSpecificTankOperations) {
                    console.log(`Using weight for tank ${unloadingItem.tank.tankName}: ${weightValue.toNumber()}`);
                }
            }
        }
    });

    if (logSpecificTankOperations) {
        console.log(`Total aggregated ship figure for regular unloading operation: ${aggregatedShipFigure.toNumber()}`);
    }

    return aggregatedShipFigure;
};

const calculateUnloadingShipFigureForTank = (
    stowagePlan: StowagePlan,
    unloadingOperation: RotationCargoOperation,
    unloadingItem: Stowage
): Decimal => {
    // Find the previous operation for the same cargo
    const previousOperation = findPreviousOperationForCargo(stowagePlan, unloadingOperation);

    if (!previousOperation) {
        if (logSpecificTankOperations) {
            console.log(`No previous operation found for cargo ${unloadingOperation.cargoGuid}, using unloading weight`);
        }
        return unloadingItem.weight ? unloadingItem.weight.abs() : new Decimal(0);
    }

    // Find the related stowage item in the previous operation for the same tank
    const previousStowageItem = stowagePlan.stowageItems.find(
        (item) => item.cargoOperationGuid === previousOperation.cargoOperationGuid && item.tank?.tankId === unloadingItem.tank?.tankId
    );

    if (!previousStowageItem) {
        if (logSpecificTankOperations) {
            console.log(`No previous stowage item found for tank ${unloadingItem.tank?.tankName}, using unloading weight`);
        }
        return unloadingItem.weight ? unloadingItem.weight.abs() : new Decimal(0);
    }

    // Calculate ship figure: previous operation ship figure - current unloading amount
    const previousShipFigure =
        previousStowageItem.shipFigure !== null && previousStowageItem.shipFigure !== undefined
            ? new Decimal(previousStowageItem.shipFigure)
            : previousStowageItem.weight
                ? previousStowageItem.weight.abs()
                : new Decimal(0);

    const unloadingAmount = unloadingItem.weight ? unloadingItem.weight.abs() : new Decimal(0);
    const calculatedShipFigure = previousShipFigure.minus(unloadingAmount);

    if (logSpecificTankOperations) {
        console.log(
            `Tank ${unloadingItem.tank?.tankName}: Previous ship figure: ${previousShipFigure.toNumber()}, ` +
            `Unloading amount: ${unloadingAmount.toNumber()}, ` +
            `Calculated ship figure: ${calculatedShipFigure.toNumber()}`
        );
    }

    // Ensure we don't return negative ship figures
    return calculatedShipFigure.gt(0) ? calculatedShipFigure : new Decimal(0);
};

const findPreviousOperationForCargo = (stowagePlan: StowagePlan, currentOperation: RotationCargoOperation): RotationCargoOperation | null => {
    // Get all operations for the same cargo, sorted by sequence/order
    const cargoOperations = sortCargoOperationsBySequence(getCargoOperationForCargo(stowagePlan, currentOperation.cargo));

    const currentIndex = cargoOperations.findIndex((op) => op.cargoOperationGuid === currentOperation.cargoOperationGuid);

    if (currentIndex > 0) {
        return cargoOperations[currentIndex - 1];
    }

    return null;
};

const checkIfLastUnloadingOperation = (stowagePlan: StowagePlan, unloadingOperation: RotationCargoOperation): boolean => {
    // Get all unloading operations for the same cargo, sorted by sequence/order
    const unloadingOperations = sortCargoOperationsBySequence(
        getCargoOperationForCargo(stowagePlan, unloadingOperation.cargo).filter((op) => op.operationType === 'Unloading')
    );

    // Check if this operation is the last in the sorted list
    const lastOperation = unloadingOperations[unloadingOperations.length - 1];
    const isLast = lastOperation?.cargoOperationGuid === unloadingOperation.cargoOperationGuid;

    if (logSpecificTankOperations) {
        console.log(`Operation ${unloadingOperation.cargoOperationGuid} is ${isLast ? 'LAST' : 'NOT LAST'} unloading operation`);
    }

    return isLast;
};

/**
 * Update the hasAdjustedShipFigure function to handle transit operations
 */
export const hasAdjustedShipFigure = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): boolean => {
    const relatedStowageItems = stowagePlan.stowageItems.filter((item) => item.cargoOperationGuid === cargoOperation.cargoOperationGuid);

    if (cargoOperation.operationType === 'Loading') {
        return relatedStowageItems.some((item) => item.shipFigure !== null && item.shipFigure !== undefined && item.isShipFigureAdjusted === true);
    } else if (cargoOperation.operationType === 'Unloading') {
        // For unloading, consider it adjusted if any ship figure is set (regardless of isShipFigureAdjusted flag)
        return relatedStowageItems.some((item) => item.shipFigure !== null && item.shipFigure !== undefined);
    } else if (cargoOperation.operationType === 'Transit') {
        // For transit operations, check if tank content has ship figures set
        const berthCall = cargoOperation.berthCall;
        if (!berthCall) {
            return false;
        }

        return relatedStowageItems.some((stowageItem) => {
            if (!stowageItem.tank) {
                return false;
            }

            const tankContent = getTankContentInBerthCall(stowagePlan, berthCall, stowageItem.tank);
            if (tankContent) {
                const cargoContent = tankContent.cargoContents.find((content) => content.cargo?.cargoGuid === cargoOperation.cargoGuid);

                return cargoContent?.shipFigure !== null && cargoContent?.shipFigure !== undefined;
            }

            return false;
        });
    }

    return false;
};

export const calculateUnloadingDifference = (stowagePlan: StowagePlan, unloadingCargoOperation: RotationCargoOperation): Decimal => {
    if (unloadingCargoOperation.operationType !== 'Unloading') {
        console.warn(`calculateUnloadingDifference called with non-unloading operation: ${unloadingCargoOperation.operationType}`);
        return new Decimal(0);
    }

    // Get all operations for the same cargo
    const allCargoOperations = getCargoOperationForCargo(stowagePlan, unloadingCargoOperation.cargo);

    // Separate loading and unloading operations
    const loadingOperations = allCargoOperations.filter((operation) => operation.operationType === 'Loading');
    const unloadingOperations = allCargoOperations.filter((operation) => operation.operationType === 'Unloading');

    // Calculate total loaded weight across all loading operations
    let totalLoadedWeight = new Decimal(0);
    loadingOperations.forEach((loadingOperation) => {
        const loadedWeight = stowedWeightInTanks(stowagePlan, loadingOperation);
        totalLoadedWeight = totalLoadedWeight.plus(loadedWeight);
    });

    // Calculate total unloaded weight across all unloading operations
    let totalUnloadedWeight = new Decimal(0);
    unloadingOperations.forEach((unloadingOperation) => {
        const unloadedWeight = stowedWeightInTanks(stowagePlan, unloadingOperation).abs(); // Use abs() since unloading weights are typically negative
        totalUnloadedWeight = totalUnloadedWeight.plus(unloadedWeight);
    });

    // Calculate difference: total loaded - total unloaded
    const difference = totalLoadedWeight.minus(totalUnloadedWeight);
    return difference;
};
